import React from "react";
import { Link } from "react-router-dom";
import styles from "../InverluxApps.module.css";

const GmailCollapseAllPrivacy = () => (
  <section className={styles.policySection}>
    <div className={styles.policyWidth}>
      <p className={styles.eyebrow}>Collapse All for Gmail</p>
      <h1>Privacy Policy</h1>
      <p className={styles.policyDate}>Last updated: August 28, 2026</p>
      <p className={styles.pageIntro}>
        This Privacy Policy explains the data practices of the Collapse All for
        Gmail Chrome extension (the “Extension”), developed and published by
        Inverlux LLC (“Inverlux,” “we,” “us,” or “our”). This policy applies to
        Version 0.1 of the Extension.
      </p>

      <div className={styles.policySummary}>
        <strong>Privacy summary</strong>
        <p>
          The Extension works locally in your browser. It does not collect,
          store, sell, or transmit Gmail content, and it does not use accounts,
          analytics, tracking, an external backend, or extension-initiated
          network requests.
        </p>
      </div>

      <article className={styles.policyContent}>
        <h2>1. What the Extension does</h2>
        <p>
          Collapse All for Gmail adds a Collapse all control to Gmail
          conversation views. The control remains available while a user
          scrolls through a conversation and collapses only messages the
          Extension can confidently identify as expanded. It does not appear
          in the inbox or other message-list views.
        </p>
        <p>
          The Extension uses Chrome’s Manifest V3 extension platform and
          interacts with Gmail’s page Document Object Model (DOM) only as
          necessary to identify conversation interface state, place its
          control, and collapse expanded messages.
        </p>

        <h2>2. Information we do not collect</h2>
        <p>The Extension does not collect, store, or transmit:</p>
        <ul>
          <li>Email subjects or message bodies</li>
          <li>Senders, recipients, or email addresses</li>
          <li>Attachments or attachment contents</li>
          <li>Gmail account content or conversation content</li>
          <li>Browsing history</li>
          <li>Usage, analytics, advertising, or tracking data</li>
        </ul>
        <p>
          The Extension does not require or create an Inverlux account. Because
          the current version does not collect information, Inverlux does not
          use Extension data to identify users or build user profiles.
        </p>

        <h2>3. Local processing</h2>
        <p>
          The Extension’s processing occurs locally in the user’s browser. Its
          DOM interaction is limited to determining the Gmail conversation UI
          state needed to provide the Collapse all feature. Gmail content is
          not sent to Inverlux as part of this processing.
        </p>

        <h2>4. Website access and permissions</h2>
        <p>
          The Extension’s website access is limited to{" "}
          <code>mail.google.com</code>. This access is required so the Extension
          can integrate the Collapse all control with Gmail’s conversation
          interface and recognize which messages are expanded. The Extension
          is not designed to run on unrelated websites.
        </p>

        <h2>5. Extension storage</h2>
        <p>
          If the Extension stores settings, those settings remain in Chrome’s
          local extension storage on the user’s device. They are not transmitted
          to Inverlux. The Extension does not use its storage for Gmail content.
        </p>

        <h2>6. Network requests, analytics, and backend services</h2>
        <p>
          The current version of the Extension initiates no network requests.
          It has no external Inverlux backend or server connection and includes
          no analytics, tracking pixels, advertising technology, or third-party
          telemetry.
        </p>
        <p>
          Gmail, Chrome, the Chrome Web Store, and other services operated by
          Google may make their own network requests or process information
          under Google’s terms and privacy policies. Those services are
          separate from the Extension and are not controlled by Inverlux.
        </p>

        <h2>7. Sharing and sale of information</h2>
        <p>
          The Extension does not collect user information or Gmail content, so
          Inverlux does not sell, rent, disclose, or share such information
          with advertisers, data brokers, or other third parties.
        </p>

        <h2>8. Data security</h2>
        <p>
          The Extension is intentionally designed to minimize data exposure by
          processing Gmail conversation UI state locally and not transmitting
          Gmail content to Inverlux. Users should continue to protect their
          Google accounts, browsers, and devices using appropriate security
          practices.
        </p>

        <h2>9. Changes to this policy</h2>
        <p>
          We may update this Privacy Policy if the Extension’s functionality or
          data practices change. Any updated policy will be posted on this page
          with a revised “Last updated” date. If a future version introduces a
          materially different data practice, the policy will be updated before
          that practice is used.
        </p>

        <h2>10. Contact</h2>
        <p>
          Questions about this Privacy Policy or the Extension’s privacy
          practices may be sent to:
        </p>
        <p>
          <strong>Inverlux LLC</strong>
          <br />
          Email:{" "}
          <a href="mailto:support@inverlux.com">support@inverlux.com</a>
        </p>

        <div className={styles.policyDisclaimer}>
          Gmail is a trademark of Google LLC. Collapse All for Gmail is
          independently developed by Inverlux LLC and is not affiliated with or
          endorsed by Google.
        </div>
      </article>

      <div className={styles.policyLinks}>
        <Link to="/gmail-collapse-all">Back to Collapse All for Gmail</Link>
        <Link to="/support">Contact support</Link>
      </div>
    </div>
  </section>
);

export default GmailCollapseAllPrivacy;
